
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, Users, Edit, List, Save } from "lucide-react";
import { StudentWithAttendance } from "@/types/attendance";
import { LateRecord } from "@/hooks/useLateAttendance";
import { memo, useCallback } from "react";
import { Student } from "@/types/student";

interface LateStudentsListProps {
  studentsWithAttendance: Student[];
  selectedGrade: string;
  selectedSection: string;
  selectedStage: string;
  selectedDate: string;
  hasMultipleStages: boolean;
  hasExistingAttendance: boolean;
  isEditMode: boolean;
  studentsLength: number;
  loading?: boolean;
  lateChanges: {[studentId: string]: 'morning_lineup' | 'after_lineup' | 'period_1' | 'period_2' | 'period_3' | 'period_4' | 'period_5' | 'period_6' | 'period_7' | null};
  onStudentLateStatusChange: (studentId: string, lateType: 'morning_lineup' | 'after_lineup' | 'period_1' | 'period_2' | 'period_3' | 'period_4' | 'period_5' | 'period_6' | 'period_7' | null) => void;
  getStageDisplayName: (stage: any) => string;
  getStudentLateRecord: (studentId: string, date: string, period?: string) => LateRecord | undefined;
  onSaveAttendance?: () => void;
  isSaving?: boolean;
}

const lateOptions = [
  { value: null, label: "غير متأخر", color: "text-green-600" },
  { value: "morning_lineup", label: "متأخر عن الاصطفاف الصباحي", color: "text-secondary" },
  { value: "after_lineup", label: "حضر بعد الاصطفاف مباشرة", color: "text-secondary" },
  { value: "period_1", label: "حضر أثناء الحصة الأولى", color: "text-secondary" },
  { value: "period_2", label: "حضر أثناء الحصة الثانية", color: "text-secondary" },
  { value: "period_3", label: "حضر أثناء الحصة الثالثة", color: "text-secondary" },
  { value: "period_4", label: "حضر أثناء الحصة الرابعة", color: "text-secondary" },
  { value: "period_5", label: "حضر أثناء الحصة الخامسة", color: "text-secondary" },
  { value: "period_6", label: "حضر أثناء الحصة السادسة", color: "text-secondary" },
  { value: "period_7", label: "حضر أثناء الحصة السابعة", color: "text-secondary" },
];

export const LateStudentsList = memo(({
  studentsWithAttendance,
  selectedGrade,
  selectedSection,
  selectedStage,
  selectedDate,
  hasMultipleStages,
  hasExistingAttendance,
  isEditMode,
  studentsLength,
  loading = false,
  lateChanges,
  onStudentLateStatusChange,
  getStageDisplayName,
  getStudentLateRecord,
  onSaveAttendance,
  isSaving = false
}: LateStudentsListProps) => {
  const canModify = !hasExistingAttendance || isEditMode;

  if (studentsLength === 0) {
    return (
      <Card className="bg-white/90 backdrop-blur-sm border-white/20 shadow-lg">
        <CardContent className="p-8 text-center">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">لا توجد بيانات طلاب</h3>
          <p className="text-gray-500 mb-4">يرجى إضافة طلاب من صفحة إدارة الطلاب أولاً</p>
        </CardContent>
      </Card>
    );
  }

  // لا تعرض رسالة "اختر الصف والشعبة" أثناء التحميل
  if (!selectedGrade || !selectedSection) {
    if (loading) {
      return null; // لا تعرض شيء أثناء التحميل
    }
    
    return (
      <Card className="bg-white/90 backdrop-blur-sm border-white/20 shadow-lg">
        <CardContent className="p-8 text-center">
          <Clock className="h-12 w-12 text-secondary mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">اختر الصف والشعبة</h3>
          <p className="text-gray-500">قم بتحديد الصف والشعبة لعرض قائمة الطلاب</p>
        </CardContent>
      </Card>
    );
  }

  if (studentsWithAttendance.length === 0) {
    return (
      <Card className="bg-white/90 backdrop-blur-sm border-white/20 shadow-lg">
        <CardContent className="p-8 text-center">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">لا توجد طلاب في هذا الصف والشعبة</h3>
          <p className="text-gray-500">
            لا يوجد طلاب في {selectedGrade} - {selectedSection}
            {hasMultipleStages && selectedStage && ` - ${getStageDisplayName(selectedStage)}`}
          </p>
        </CardContent>
      </Card>
    );
  }

  const periodText = '';
  const stageText = hasMultipleStages && selectedStage ? ` - ${getStageDisplayName(selectedStage)}` : '';

  return (
    <Card className="bg-white/90 backdrop-blur-sm border-white/20 shadow-lg h-full flex flex-col mb-6">
      <CardHeader className="pb-4 bg-secondary text-white rounded-t-lg flex-shrink-0">
        <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2 text-white text-base sm:text-lg">
            <List className="h-5 w-5" />
            <span className="truncate">
              قائمة طلاب {selectedGrade} - {selectedSection}{stageText}{periodText}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isEditMode && Object.keys(lateChanges).length > 0 && onSaveAttendance && (
              <Button 
                variant="secondary" 
                size="sm" 
                className="bg-white hover:bg-white/90 text-secondary border-white text-xs sm:text-sm whitespace-nowrap"
                onClick={onSaveAttendance}
                disabled={isSaving}
              >
                <Save className="h-3 w-3 sm:h-4 sm:w-4 ml-1 sm:ml-2" />
                {isSaving ? "جاري الحفظ..." : "حفظ التأخر"}
              </Button>
            )}
            {!isEditMode && hasExistingAttendance && (
              <Button 
                variant="secondary" 
                size="sm" 
                className="bg-white/20 hover:bg-white/30 text-white border-white/30 text-xs sm:text-sm whitespace-nowrap"
                onClick={() => window.dispatchEvent(new CustomEvent('enableLateEditMode'))}
              >
                <Edit className="h-3 w-3 sm:h-4 sm:w-4 ml-1 sm:ml-2" />
                تعديل التأخر
              </Button>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <div className="border-t-2 border-gray-300 flex-shrink-0"></div>
      <CardContent className="pt-6 flex-1 overflow-auto">
        <div className="overflow-x-auto pb-4">
          <table className="w-full border-collapse border-2 border-r-2 border-b-2 border-gray-300 rounded-lg overflow-hidden">
            <thead>
              <tr className="border-b border-gray-300 bg-gray-200">
                <th className="text-right p-4 font-medium text-gray-700 w-16 border-l border-r border-gray-300">م</th>
                <th className="text-right p-4 font-medium text-gray-700 border-l border-gray-300">اسم الطالب</th>
                <th className="text-center p-4 font-medium text-gray-700 w-72">حالة التأخر</th>
              </tr>
            </thead>
            <tbody>
              {studentsWithAttendance.map((student, index) => {
                const pendingChange = lateChanges[student.id];
                const currentLateRecord = getStudentLateRecord(student.id, selectedDate, undefined);
                const effectiveLateType = pendingChange !== undefined ? pendingChange : currentLateRecord?.lateType;
                const isLate = effectiveLateType !== null && effectiveLateType !== undefined;
                
                const handleLateStatusChange = (value: string) => {
                  const lateType = value === 'null' ? null : value as any;
                  onStudentLateStatusChange(student.id, lateType);
                };

                return (
                  <tr 
                    key={student.id} 
                    className={`${index === studentsWithAttendance.length - 1 ? 'border-b-2 border-gray-300' : 'border-b border-gray-300'} hover:bg-gray-50 ${
                      isLate ? 'bg-orange-50/50' : 'bg-green-50/50'
                    } ${pendingChange !== undefined ? 'ring-2 ring-orange-300' : ''}`}
                  >
                    <td className="p-4 text-right text-gray-600 font-medium border-l border-r border-gray-300">
                      {index + 1}
                    </td>
                    <td className="p-4 text-right border-l border-gray-300">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{student.name}</span>
                        {pendingChange !== undefined && (
                          <span className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-full">
                            تغيير معلق
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-center border-gray-300">
                      <div className="flex items-center justify-center">
                        {canModify ? (
                          <Select
                            value={effectiveLateType || 'null'}
                            onValueChange={handleLateStatusChange}
                          >
                            <SelectTrigger className="w-56 bg-white border-gray-300">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-300 z-50">
                              {lateOptions.map((option) => (
                                <SelectItem 
                                  key={option.value || 'null'} 
                                  value={option.value || 'null'}
                                  className={`hover:bg-gray-100 ${option.color}`}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`px-3 py-1 rounded-full text-sm ${
                            isLate ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {effectiveLateType ? 
                              lateOptions.find(opt => opt.value === effectiveLateType)?.label || 'متأخر' 
                              : 'غير متأخر'
                            }
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
});
